import React, { useMemo, useState } from "react";
import "./styles.css";
import analysis from "./data/needs";
import capabilityField from "./data/capabilityDetails";
import conditionEvidence from "./data/conditionEvidence";
import CapabilityWorkspace from "./features/needs/application/CapabilityWorkspace";

import ResolutionTraceFieldPage from './features/v25/resolution-field/ResolutionTraceFieldPage.jsx';
import ResearchEcologyKanban from "./features/research-ops/ResearchEcologyKanban";
// NEEDS_PRESENTATION_LITE_SEMANTIC_ZOOM_V3

const FIELD_LABELS = {
  "ROF-S01": "Relasjonell deltakelse / agency",
  "ROF-S02": "Uttrykk / anvendelse / bidrag",
  "ROF-S03": "Tilgang / utvikling",
  "ROF-S04": "Profesjonell identitet / handlingsrom",
  "ROF-S05": "Kollegial utforskning av praksis",
  "ROF-S06": "Vedvarende profesjonell utforskning",
};

const CAPABILITY_LABELS = {
  "capability:belong_and_connect": "Høre til og knytte seg til andre",
  "capability:navigate_transitions_and_choices": "Navigere overganger og valg",
  "capability:participate_and_exercise_voice": "Delta og bruke egen stemme",
  "capability:communicate_and_make_meaning": "Kommunisere og skape mening",
  "capability:contribute_through_work_and_community": "Bidra gjennom arbeid og fellesskap",
  "capability:access_learning_and_support": "Få tilgang til læring og støtte",
  "capability:learn_and_develop_capability": "Lære og utvikle kapasitet",
  "capability:develop_professional_practice": "Utvikle profesjonell praksis",
  "capability:reciprocal_examination_of_professional_practice": "Gjensidig undersøkelse av profesjonell praksis",
  "capability:sustained_professional_inquiry": "Vedvarende profesjonell utforskning",
  "capability:integrated_professional_identity_development": "Integrert profesjonell identitetsutvikling",
};

const CONDITION_NB = {
  support: "støtte", community: "fellesskap", relationships: "relasjoner", resources: "ressurser",
  services: "tjenester", information: "informasjon", language: "språk", curriculum: "læreplan",
  communication: "kommunikasjon", technology: "teknologi", training: "opplæring", time: "tid",
  "Sufficient time and continuity for professional learning": "tid + kontinuitet",
  "Structured professional-development support": "strukturert støtte",
  "Reciprocal trust and confidence": "gjensidig tillit",
  "Open, collegial professional relationships": "åpne kollegiale relasjoner",
  "Peer collaboration and interaction": "kollegial læring / samarbeid",
  "Reflective dialogue around practice": "refleksiv dialog",
  "Inquiry into professional practice": "utforskning av praksis",
  "Professional agency and self-direction": "profesjonell agency",
  "Outcome, accountability or control pressure": "resultat- / kontrollpress",
};

function nbCondition(value) { return CONDITION_NB[value] || value; }
function capLabel(id, fallback) { return CAPABILITY_LABELS[id] || fallback || id.replace("capability:", "").replaceAll("_", " "); }
function cleanSupport(s) {
  if (s === "core_reactor_support") return "Direkte støtte";
  if (s === "partial_support") return "Delvis";
  if (s === "shadow_context_not_support") return "Kontekst · ikke støtte";
  return s || "Forskningsrot";
}
function isSupport(source) { return source.support_class !== "shadow_context_not_support"; }
function primaryLink(source) { return (source.links || [])[0]?.url || (source.doi ? `https://doi.org/${source.doi}` : null); }
function deepKey(root, capability) { return `${root}|${capability}`; }

function capabilityDetail(id) {
  return (capabilityField.capabilities || []).find(c => c.id === id) || null;
}

function capabilityEdges(id) {
  return (capabilityField.strict_edges || [])
    .filter(e => e.source === id || e.target === id)
    .map(e => ({ ...e, other: e.source === id ? e.target : e.source }))
    .sort((a,b) => b.weight - a.weight);
}

function scoreParts(a, b) {
  const direct = `${a}|${b}`;
  const reverse = `${b}|${a}`;
  return capabilityField.strict_score_parts?.[direct] || capabilityField.strict_score_parts?.[reverse] || null;
}

function allConditionCounts() {
  const counts = new Map();
  (capabilityField.capabilities || []).forEach(c => {
    const seen = new Set((c.conditions || []).map(nbCondition));
    seen.forEach(name => counts.set(name, (counts.get(name) || 0) + 1));
  });
  return counts;
}

function ascentLabel(id) {
  return ({
    "ascent:access_stability": "Tilgang / stabilitet",
    "ascent:participation_belonging": "Deltakelse / tilhørighet",
    "ascent:agency_navigation": "Agency / navigasjon",
    "ascent:capability_mastery": "Utvikling / mestring",
    "ascent:contribution_future": "Bidrag / framtid",
  })[id] || id;
}

function scopeLabel(value) {
  return ({
    "Families / parents": "familier / foresatte",
    "Students · unspecified": "studenter",
    "School students / pupils": "elever",
    "Teachers": "lærere",
    "Teacher educators": "lærerutdannere",
    "Student teachers": "lærerstudenter",
    "Professional participants": "profesjonelle deltakere",
  })[value] || value;
}

function Stat({ big, children }) { return <div className="stat"><strong>{big}</strong><span>{children}</span></div>; }

function Breadcrumb({ stage, reset, backToField, backToCapability, backToResearch, backToStudy }) {
  const items = [
    ["overview", "Landskap", reset],
    ["field", "Felt", backToField],
    ...(backToCapability ? [["capability", "Kapabilitet", backToCapability]] : []),
    ["research", "Forskning", backToResearch],
    ["study", "Studie", backToStudy],
    ["evidence", "Kildespor", null],
  ];
  const order = items.map(x => x[0]);
  const active = order.indexOf(stage);
  return <div className="breadcrumbs">{items.map(([id,label,fn],i) => i <= active ? <React.Fragment key={id}>
    {i>0 && <span className="crumb-arrow">›</span>}
    <button className={id===stage?"crumb active":"crumb"} onClick={fn || undefined}>{label}</button>
  </React.Fragment> : null)}</div>;
}

function FieldResearchBadge({ field }) {
  const support = field.sources.filter(isSupport).length;
  return <div className="research-badge"><span className="pulse"/><strong>{field.independent_roots}</strong> forskningsrøtter <span>· {support} støttende</span></div>;
}

function Landscape({ pov, onSelect, selectedId }) {
  const levels = analysis.pov[pov] || analysis.pov.student;
  const fieldById = Object.fromEntries(analysis.fields.map(f => [f.id,f]));
  return <div className={`landscape ${pov}`}>
    <div className="horizon">
      <div className="eyebrow">BIDRAGSHORISONT</div>
      <div className="horizon-pills"><span>Arbeid</span><span>Videre læring</span><span>Fellesskap</span></div>
      <div className="orientation-note">retning for navigasjon · ikke en bevist årsakskjede</div>
    </div>
    <div className="terraces">
      {levels.map((level, index) => {
        const field=fieldById[level.field]; const selected=selectedId===level.id;
        return <button key={level.id} className={`terrace t${index+1} ${selected?"selected":""}`} onClick={() => onSelect(level)}>
          <div className="terrace-copy"><span className="terrace-kicker">{index===0?"høyere orientering":""}</span><strong>{level.title}</strong><small>{level.subtitle}</small></div>
          <div className="terrace-receipt"><b>{field?.independent_roots ?? "–"}</b><span>røtter</span></div>
          <span className="zoom-hint">Utforsk · zoom inn →</span>
        </button>
      })}
    </div>
    <div className="s0"><span>S0</span><b>Nåsituasjon</b></div>
  </div>;
}

function CrossConditions({ pov }) {
  const ids = pov === "teacher" ? ["ROF-S04","ROF-S05","ROF-S06"] : ["ROF-S01","ROF-S02","ROF-S03"];
  const fields=analysis.fields.filter(f=>ids.includes(f.id));
  const counts=new Map();
  fields.forEach(f=>f.conditions.forEach(c=>counts.set(nbCondition(c),(counts.get(nbCondition(c))||0)+1)));
  const items=[...counts.entries()].sort((a,b)=>b[1]-a[1]).slice(0,10);
  return <section className="condition-river"><div><span className="eyebrow">GJENTAKENDE BETINGELSER</span><p>Forhold som går igjen på tvers av deler av landskapet.</p></div><div className="condition-chips">{items.map(([name,count])=><span key={name} className={count>1?"condition-chip cross":"condition-chip"}>{name}{count>1?<sup>{count}</sup>:null}</span>)}</div></section>;
}

function FieldPanel({ level, field, capabilityId, onCapability, onResearch }) {
  const levelCaps = new Set(level.capability_ids || []);
  const supportSources = field.sources.filter(isSupport);
  const allCaps = field.capabilities || [];
  return <section className="zoom-card field-panel">
    <div className="zoom-heading">
      <div><span className="step">ZOOM 1 · FELT</span><h2>{level.title}</h2><p>{level.subtitle}</p></div>
      <FieldResearchBadge field={field}/>
    </div>

    <div className="field-intro">
      <div>
        <span className="eyebrow">FREMTREDENDE FELT</span>
        <p>Dette Ascent-nivået peker inn i et rikere analytisk mulighetsfelt. Velg en kapabilitet for å se betingelser, nærliggende kapabiliteter og forskningsrøtter.</p>
      </div>
      <div className="field-mini-stats">
        <span><b>{allCaps.length}</b> kapabiliteter i feltet</span>
        <span><b>{field.conditions.length}</b> feltbetingelser</span>
        <span><b>{supportSources.length}</b> støttende røtter i håndoffen</span>
      </div>
    </div>

    <div className="capability-tiles">
      {allCaps.map(c => {
        const inLevel = levelCaps.has(c.id);
        return <button key={c.id} className={`capability-tile ${inLevel?"ascent-member":""}`} onClick={()=>onCapability(c.id)}>
          <span className="capability-tile-kicker">{inLevel ? "I DETTE ASCENT-NIVÅET" : "SAMME FREMTREDENDE FELT"}</span>
          <b>{capLabel(c.id,c.label)}</b>
          <small>{inLevel ? "Klikk for å åpne den interne konfigurasjonen" : "Nabokapabilitet i samme analytiske felt"}</small>
          <span className="tile-zoom">Zoom inn →</span>
        </button>;
      })}
    </div>

    <div className="field-condition-preview">
      <span className="eyebrow">FELTBETINGELSER · KOMPRIMERT VISNING</span>
      <div className="condition-chips large">{field.conditions.slice(0,12).map(c=><span className="condition-chip" key={c}>{nbCondition(c)}</span>)}</div>
    </div>

    <div className="research-strip">
      <div><b>{field.independent_roots} uavhengige forskningsrøtter</b><span>{field.institution_count ? ` · ${field.institution_count} verifiserte affiliation-institusjoner · ${field.country_count} land i denne felt-handoff-en` : " · institusjoner vises når metadata er verifisert"}</span></div>
      <button className="secondary" onClick={onResearch}>Feltets forskning →</button>
    </div>
  </section>;
}


function conditionDetail(capabilityId, raw) {
  return conditionEvidence?.[capabilityId]?.[raw] || {
    description: `I denne kapabilitetsprofilen opptrer «${raw}» som en betingelse som kan forme hva som blir mulig.`,
    references: [],
  };
}

function depthLabel(depth) {
  if (depth === "atom_proposition_relation_and_source_passage") return "evidens + relasjon + kildepassasje";
  if (depth === "v21_structure_excerpt_only") return "studie-/abstraktstruktur";
  return "kapabilitetsprofil";
}

function ConditionNode({ capabilityId, item, active, onSelect }) {
  const detail = conditionDetail(capabilityId, item.raw);
  const refCount = detail.references?.length || 0;
  return <button type="button" className={`orbit-node condition-button ${item.count>1?"recurring":""} ${active?"active":""}`} onClick={()=>onSelect(active?null:item.raw)}>
    <span>{item.label}</span>
    <small>{refCount ? `${refCount} kilde${refCount===1?"":"r"} · åpne` : "forklaring · åpne"}</small>
    {item.count>1 && <sup>{item.count} profiler</sup>}
  </button>;
}

function ConditionEvidenceDrawer({ capabilityId, raw, onClose }) {
  if (!raw) return null;
  const detail = conditionDetail(capabilityId, raw);
  const refs = detail.references || [];
  const hasTextMatch = refs.some(r=>r.match === "text");
  return <div className="condition-evidence-drawer">
    <div className="condition-evidence-head">
      <div><span className="eyebrow">BETINGELSE · FORSKNINGSFORANKRING</span><h3>{nbCondition(raw)}</h3></div>
      <button type="button" className="quiet-close" onClick={onClose}>Lukk ×</button>
    </div>
    <p className="condition-explanation">{detail.description}</p>
    <div className="condition-source-note">
      <b>{hasTextMatch ? "Kilder med tekstlig treff" : "Kilder i kapabilitetsprofilen"}</b>
      <span>{hasTextMatch
        ? "Betingelsen eller et nært synonym finnes i eksisterende evidens/studiestruktur. Dette er dokumentasjon av forankring, ikke en kausal påstand."
        : "Her vises forskningsrøtter som bærer kapabilitetsprofilen. Handoff-en har ikke et eksplisitt teksttreff på selve betingelsesordet."}</span>
    </div>
    {refs.length ? <ol className="harvard-list">{refs.map((ref,i)=><li key={ref.root_id+String(i)}>
      {ref.url ? <a href={ref.url} target="_blank" rel="noreferrer">{ref.harvard}</a> : <span>{ref.harvard}</span>}
      <small>{ref.root_id} · {depthLabel(ref.depth)}</small>
    </li>)}</ol> : <div className="quiet-note">Ingen separat kildehenvisning er tilgjengelig for denne betingelsen i den kompakte presentasjonshandoff-en.</div>}
  </div>;
}

function CapabilityPanel({ level, field, capabilityId, onCapability, onResearch }) {
  const detail = capabilityDetail(capabilityId);
  const counts = useMemo(() => allConditionCounts(), []);
  const [selectedCondition,setSelectedCondition]=useState(null);
  const [selectedNeighbor,setSelectedNeighbor]=useState(null);
  if (!detail) return <section className="zoom-card"><b>Ingen detaljprofil funnet for denne kapabiliteten.</b></section>;

  const conditions = (detail.conditions || []).map(raw => ({ raw, label: nbCondition(raw), count: counts.get(nbCondition(raw)) || 1 }));
  const recurring = conditions.filter(x => x.count > 1).sort((a,b) => b.count-a.count || a.label.localeCompare(b.label));
  const situated = conditions.filter(x => x.count <= 1);
  const edges = capabilityEdges(capabilityId);
  const ascent = Object.entries(detail.posthoc_ascent || {}).sort((a,b)=>b[1]-a[1]);
  const sameField = (field.capabilities || []).filter(c => c.id !== capabilityId);
  const rootCount = new Set([...(detail.core_roots||[]), ...(detail.partial_roots||[])]).size;

  return <section className="zoom-card capability-panel">
    <div className="zoom-heading">
      <div><span className="step">ZOOM 2 · KAPABILITET / KONFIGURASJON</span><h2>{capLabel(capabilityId,detail.label)}</h2><p>Her åpnes feltet. Klikk på en betingelse for en kort forklaring og konkrete kilder; analytiske forbindelser vises uten å bli gjort om til en kausal trapp.</p></div>
      <div className="config-badge"><strong>{rootCount}</strong><span>unike forskningsrøtter med direkte eller delvis støtte i kapabilitetsprofilen</span></div>
    </div>

    <div className="capability-lens">
      <div className="condition-orbit left">
        <span className="orbit-title">DELTE BETINGELSER</span>
        <small>går igjen i flere kapabilitetsprofiler · klikk for kilder</small>
        <div className="orbit-list">
          {recurring.slice(0,9).map(c=><ConditionNode key={c.raw} capabilityId={capabilityId} item={c} active={selectedCondition===c.raw} onSelect={setSelectedCondition}/>)}
        </div>
      </div>

      <div className="capability-core-wrap">
        <div className="capability-core">
          <span className="eyebrow">HVA MÅ BLI MER MULIG?</span>
          <h3>{capLabel(capabilityId,detail.label)}</h3>
          <div className="core-stats">
            <span><b>{(detail.core_roots||[]).length}</b> direkte støtte</span>
            <span><b>{(detail.partial_roots||[]).length}</b> delvis støtte</span>
            {(detail.shadow_roots||[]).length>0 && <span className="shadow"><b>{detail.shadow_roots.length}</b> kontekst · ikke støtte</span>}
          </div>
          <div className="scope-row">{(detail.scopes||[]).map(s=><span key={s}>{scopeLabel(s)}</span>)}</div>
        </div>
        <div className="orientation-caption">kapabiliteten er det stabile sentrum i dette utsnittet</div>
      </div>

      <div className="condition-orbit right">
        <span className="orbit-title">ØVRIGE BETINGELSER</span>
        <small>til stede i akkurat denne kapabilitetsprofilen · klikk for kilder</small>
        <div className="orbit-list">
          {(situated.length?situated:conditions.slice(recurring.length)).slice(0,9).map(c=><ConditionNode key={c.raw} capabilityId={capabilityId} item={c} active={selectedCondition===c.raw} onSelect={setSelectedCondition}/>)}
        </div>
      </div>
    </div>

    <ConditionEvidenceDrawer capabilityId={capabilityId} raw={selectedCondition} onClose={()=>setSelectedCondition(null)}/>

    {conditions.length > 18 && <details className="all-conditions"><summary>Vis alle {conditions.length} betingelser i profilen</summary><div className="condition-chips large">{conditions.map(c=><button type="button" className={c.count>1?"condition-chip cross condition-chip-button":"condition-chip condition-chip-button"} key={c.raw} onClick={()=>setSelectedCondition(c.raw)}>{c.label}{c.count>1?<sup>{c.count}</sup>:null}</button>)}</div></details>}

    <div className="nuance-grid">
      <div className="nuance-card">
        <span className="eyebrow">ANALYTISK NÆRHET · IKKE ÅRSAK</span>
        <p>Her vises kapabiliteter som ligger nær hverandre i materialet fordi de deler deler av forskningsgrunnlaget, betingelser eller målgrupper. Åpne en rad for forklaring; ingenting flytter deg før du selv velger «Åpne kapabiliteten».</p>
        {edges.length ? <div className="edge-list">{edges.map(e => {
          const parts=scoreParts(capabilityId,e.other);
          const open=selectedNeighbor===e.other;
          return <div className={`edge-item ${open?"open":""}`} key={`${e.source}|${e.target}`}>
            <button type="button" className="edge-row" onClick={()=>setSelectedNeighbor(open?null:e.other)}>
              <div><b>{capLabel(e.other)}</b><small>forskningsrøtter · betingelser · målgrupper</small></div>
              <span>{open?"−":"+"}</span>
            </button>
            {open && <div className="edge-explain">
              <p>Analysen plasserer disse kapabilitetene i nærheten av hverandre. Det er en beskrivelse av konfigurasjonen i materialet, ikke en påstand om at den ene forårsaker den andre.</p>
              {parts && <div className="plain-components"><span>felles forskningsrøtter</span><span>delte betingelser</span><span>overlappende målgrupper</span></div>}
              <div className="edge-actions"><small>analytisk nærhet {e.weight.toFixed(2)}</small><button type="button" className="secondary compact" onClick={()=>onCapability(e.other)}>Åpne kapabiliteten →</button></div>
            </div>}
          </div>;
        })}</div> : <div className="quiet-note">Ingen tydelig kapabilitetsforbindelse krysser denne profilen i den strenge forskningsvisningen. Vi tegner ikke en forbindelse analysen ikke viser.</div>}
      </div>

      <div className="nuance-card">
        <span className="eyebrow">ASCENT · POST-HOC ORIENTERING</span>
        <p>Ascent organiserer presentasjonen etter at feltet er dannet; denne visningen er ikke en utviklings- eller årsakskjede.</p>
        {ascent.length ? <div className="ascent-bars">{ascent.map(([id,value])=><div className="ascent-bar" key={id}><span>{ascentLabel(id)}</span><i><em style={{width:`${Math.min(100,value*100)}%`}}/></i><b>{value.toFixed(2)}</b></div>)}</div> : <div className="quiet-note">Ingen eksplisitt post-hoc Ascent-projeksjon er lagret for denne kapabiliteten.</div>}
      </div>
    </div>

    {sameField.length>0 && <div className="sibling-band">
      <div><span className="eyebrow">SAMME FREMTREDENDE FELT</span><p>Bytt kapabilitet her uten å gå tilbake til toppen av siden.</p></div>
      <div>{sameField.map(c=><button key={c.id} onClick={()=>onCapability(c.id)}>{capLabel(c.id,c.label)} <span>→</span></button>)}</div>
    </div>}

    <div className="research-strip">
      <div><b>Fra konfigurasjon til forskningsgrunnlag</b><span> · forskningen filtreres til den valgte kapabiliteten</span></div>
      <button className="primary" onClick={onResearch}>Se forskningen for denne kapabiliteten →</button>
    </div>
  </section>;
}

function ResearchPanel({ field, capabilityId, onStudy }) {
  const [showAll,setShowAll]=useState(false);
  const sources=field.sources.filter(isSupport).filter(s=>!capabilityId || (s.capability_routes||[]).some(r=>r.capability_id===capabilityId));
  const visible=showAll?sources:sources.slice(0,6);
  return <section className="zoom-card">
    <div className="zoom-heading"><div><span className="step">ZOOM 2 · FORSKNING</span><h2>Hvorfor tror vi dette?</h2><p>{capabilityId?capLabel(capabilityId):FIELD_LABELS[field.id]} · forskningsrøttene ligger én klikk unna analysen.</p></div><FieldResearchBadge field={field}/></div>
    {field.institution_names.length>0 && <div className="institution-band"><span className="eyebrow">VERIFISERTE RESEARCH ROOTS / FORFATTERTILKNYTNING</span><p>{field.institution_names.slice(0,10).join(" · ")}</p><small>Forfattertilknytning ≠ studielokasjon eller kvalitetsmål.</small></div>}
    <div className="paper-list">{visible.map(s=><button className="paper" key={s.root_id} onClick={()=>onStudy(s)}>
      <div className="paper-top"><span className={s.support_class==="core_reactor_support"?"support core":"support"}>{cleanSupport(s.support_class)}</span><span className="root-id">{s.root_id}</span></div>
      <strong>{s.title}</strong><p>{[...(s.authors||[]).slice(0,3), s.year].filter(Boolean).join(" · ")}</p>
      <div className="paper-route">{(s.capability_routes||[]).filter(r=>!capabilityId||r.capability_id===capabilityId).slice(0,3).map(r=><span key={r.capability_id+String(r.route)}>→ {capLabel(r.capability_id,r.capability_label)}</span>)}</div>
      <span className="open-paper">Åpne studie →</span>
    </button>)}</div>
    {sources.length>6 && <button className="secondary" onClick={()=>setShowAll(v=>!v)}>{showAll?"Vis færre":`Vis alle ${sources.length} forskningsrøtter`}</button>}
  </section>;
}

function StudyPanel({ source, capabilityId, onEvidence }) {
  const routes=(source.capability_routes||[]).filter(r=>!capabilityId||r.capability_id===capabilityId);
  const candidateKeys=routes.map(r=>deepKey(source.root_id,r.capability_id));
  const deep=candidateKeys.map(k=>analysis.deep[k]).find(Boolean) || analysis.deep[deepKey(source.root_id,capabilityId)];
  const link=primaryLink(source);
  return <section className="zoom-card study-panel">
    <span className="step">ZOOM 3 · STUDIE</span><h2>{source.title}</h2>
    <p className="study-meta">{(source.authors||[]).join(", ")}{source.year?` · ${source.year}`:""}</p>
    <div className="study-grid"><div><span className="eyebrow">DENNE STUDIEN DELTAR I</span>{routes.map((r,i)=><div className="route-row" key={i}><b>→ {capLabel(r.capability_id,r.capability_label)}</b><span>{r.support_status} · {r.route}</span></div>)}</div>
    <div><span className="eyebrow">PROVENIENS</span><p>{source.root_id} · {cleanSupport(source.support_class)}</p>{source.institutions?.length>0 && <p>{source.institutions.join(" · ")}</p>}{link && <a className="source-link" href={link} target="_blank" rel="noreferrer">Åpne original kilde ↗</a>}</div></div>
    {deep?.status === "atom_proposition_relation_and_source_passage" && <div className="evidence-ready"><div><span className="eyebrow">DYBT KILDESPOR TILGJENGELIG</span><b>{deep.depth?.["9_evidence_atom"] || deep.atoms.length} evidensatomer · {deep.depth?.["10_relation_object"] || deep.relations.length} relasjoner</b><p>Her kan du zoome helt ned til eksisterende Rxxx:Exxx → proposition → relasjon → originalpassasje.</p></div><button className="primary" onClick={()=>onEvidence(deep)}>Zoom til evidens →</button></div>}
    {deep?.status === "v21_structure_excerpt_only" && <div className="excerpt-box"><span className="eyebrow">NESTE TILGJENGELIGE DYBDE</span><b>Utdraget studiestruktur · ikke atomnivå</b>{deep.excerpts?.map((e,i)=><p key={i}>“{e.passage}”</p>)}<small>V21-strukturen beholdes eksplisitt som studiestruktur; den blir ikke fremstilt som Rxxx:Exxx-evidens.</small></div>}
    {!deep && <div className="excerpt-box"><b>Denne forskningsroten stopper ved studienivå i nåværende handoff.</b></div>}
  </section>;
}

function EvidencePanel({ deep }) {
  const [atomIndex,setAtomIndex]=useState(0);
  const atom=deep.atoms?.[atomIndex] || deep.atoms?.[0];
  const prefix=atom?.id ? atom.id+":" : "";
  const prop=(deep.propositions||[]).find(p=>p.id?.startsWith(prefix)) || deep.propositions?.[0];
  const propPrefix=prop?.id ? prop.id+":" : "";
  const rel=(deep.relations||[]).find(r=>r.id?.startsWith(propPrefix)) || deep.relations?.[0];
  return <section className="zoom-card evidence-panel">
    <div className="zoom-heading"><div><span className="step">ZOOM 4 · KILDESPOR / EVIDENS</span><h2>Vis meg hvordan dere kom frem til dette</h2><p>{deep.study?.title}</p></div><span className="microscope">⌕</span></div>
    <div className="evidence-flow">
      <div className="evidence-node source"><span className="eyebrow">EVIDENSATOM</span><b>{atom?.id || "Rxxx:Exxx"}</b><p>{atom?.passage || "Ingen atompassasje i dette kompakte utsnittet."}</p></div>
      <div className="flow-arrow"><span>dekomponering</span>↓</div>
      <div className="evidence-node"><span className="eyebrow">PROPOSITION</span><b>{prop?.id || "proposition"}</b><p>{prop?.passage || atom?.passage}</p></div>
      <div className="flow-arrow"><span>relasjonell tolkning</span>↓</div>
      <div className="evidence-node relation"><span className="eyebrow">RELASJONSOBJEKT</span>{rel ? <><div className="relation-line"><strong>{rel.source_phrase}</strong><span>↓ {rel.label}</span><strong>{rel.target_phrase}</strong></div><div className="diagnostics"><span>warrant {rel.relation_warrant ?? "–"}</span><span>parse {rel.parse_integrity ?? "–"}</span><span>role {rel.role_fidelity ?? "–"}</span></div></> : <p>Ingen relasjon i dette valgte kompaktutsnittet.</p>}</div>
      <div className="flow-arrow"><span>↶ kilde</span>↓</div>
      <div className="evidence-node source-passage"><span className="eyebrow">ORIGINAL KILDEPASSASJE</span><p>{rel?.source_passage || atom?.passage || "–"}</p><small>Kilden forblir invariant. Avstand i proveniens betyr transformasjon, ikke konfidens.</small></div>
    </div>
    {deep.atoms?.length>1 && <div className="atom-picker"><span className="eyebrow">FLERE EVIDENSATOMER I DETTE DEMOUTSNITTET</span><div>{deep.atoms.map((a,i)=><button className={i===atomIndex?"atom active":"atom"} key={a.id} onClick={()=>setAtomIndex(i)}>{a.id}</button>)}</div></div>}
  </section>;
}

function WholeSystem({ selectPov }) {
  return <section className="whole-system"><div className="whole-head"><span className="eyebrow">HELE SYSTEMET</span><h2>To lesbare terreng · ett felles forskningsgrunnlag</h2><p>Student- og lærerperspektivet holdes separate i grensesnittet, men kan sees sammen uten å gjøre Need → Need til en årsakskjede.</p></div><div className="tributaries"><button onClick={()=>selectPov("student")}><span>STUDENT</span><b>Tilgang → deltakelse → utvikling → bidrag</b><small>72 felt-røtter fordelt over tre fremvoksende felt</small></button><div className="join">⌁</div><button onClick={()=>selectPov("teacher")}><span>LÆRER</span><b>Kollegial utforskning → vedvarende utforskning → identitet</b><small>20 felt-røtter i den profesjonelle tributæren</small></button></div><CrossConditions pov="teacher"/></section>;
}

export default function App() {

  // V25_6_2_RESOLUTION_FIELD_PREVIEW_ROUTE
  const resolutionFieldPreviewPath =
    window.location.pathname.replace(/\/+$/, '') || '/';
  if (resolutionFieldPreviewPath === '/resolution-field') {
    return <ResolutionTraceFieldPage />;
  }

  const [pov,setPov]=useState("student");
  const [stage,setStage]=useState("overview");
  const [level,setLevel]=useState(null);
  const [capabilityId,setCapabilityId]=useState(null);
  const [source,setSource]=useState(null);
  const [deep,setDeep]=useState(null);
  const fieldById=useMemo(()=>Object.fromEntries(analysis.fields.map(f=>[f.id,f])),[]);
  const field=level?fieldById[level.field]:null;
  const reset=()=>{setStage("overview");setLevel(null);setCapabilityId(null);setSource(null);setDeep(null)};
  const choosePov=(next)=>{setPov(next);reset()};
  const chooseLevel=(l)=>{setLevel(l);setCapabilityId(l.capability_ids?.[0]||null);setSource(null);setDeep(null);setStage("field");setTimeout(()=>document.querySelector(".zoom-zone")?.scrollIntoView({behavior:"smooth",block:"start"}),40)};
  const chooseCapability=(id)=>{setCapabilityId(id);setSource(null);setDeep(null);setStage("capability")};
  // CHILD_B_OPS_PLANNING_ROUTE_R5
  if (window.location.pathname === "/planning" || window.location.pathname === "/planning/") {
    return <ResearchEcologyKanban />;
  }
  return <main className="app-shell">
    <header className="topbar"><div className="brand"><div className="mark">N</div><div><b>Needs Analysis</b><span>Hva må bli mer mulig?</span></div></div><nav className="pov-tabs"><button className={pov==="student"?"active":""} onClick={()=>choosePov("student")}>Student</button><button className={pov==="teacher"?"active":""} onClick={()=>choosePov("teacher")}>Lærer</button><button className={pov==="whole"?"active":""} onClick={()=>choosePov("whole")}>Hele systemet</button></nav></header>
    <section className="hero"><div><span className="eyebrow">FORSKNINGSANALYSE</span><h1>Fra forskningskorpus til et lesbart <em>mulighetslandskap.</em></h1><p>En enkel, Maslow-lignende orientering på overflaten. Kapabiliteter, betingelser, analytiske sammenhenger og forskningsproveniens ligger under og kommer frem når du zoomer.</p></div><div className="stats"><Stat big={analysis.coverage.unique_study_roots}>studierøtter regnskapsført</Stat><Stat big={analysis.coverage.states.reactor_addressable}>med tydelig kapabilitetsrute</Stat><Stat big={analysis.deep_coverage.unique_route_roots}>studier med kapabilitetskobling</Stat><Stat big={analysis.deep_coverage.deep_status_counts.atom_proposition_relation_and_source_passage}>dype evidensspor</Stat></div></section>
    <div className="research-scale"><span>FORSKNINGSGRUNNLAG</span><div className="scale-line"><i/><b>271 forskningsrøtter</b><i/><span>OpenAlex · ERIC · lokal kontekst</span><i/><span>alt uløst materiale beholdes uløst</span></div></div>
    
    {pov==="whole" ? <WholeSystem selectPov={choosePov}/> : <>
      {stage==="overview" && (
    <section className="landscape-card"><Landscape pov={pov} onSelect={chooseLevel} selectedId={level?.id}/><CrossConditions pov={pov}/></section>)}
      <section className="zoom-zone"><Breadcrumb stage={stage} reset={reset} backToField={()=>setStage("field")} backToCapability={capabilityId?()=>setStage("capability"):null} backToResearch={()=>setStage("research")} backToStudy={()=>setStage("study")}/>
        {stage==="overview" && <div className="empty-zoom"><span>↓</span><b>Velg et nivå i landskapet</b><p>Zoom endrer analytisk oppløsning — ikke bare størrelsen på grafikken.</p></div>}
        {stage==="field" && field && <FieldPanel level={level} field={field} capabilityId={capabilityId} onCapability={chooseCapability} onResearch={()=>{setCapabilityId(null);setStage("research")}}/>}
        {stage==="capability" && field && capabilityId && (
          <CapabilityWorkspace
            key={capabilityId}
            capabilityId={capabilityId}
            capabilityLabel={capLabel(capabilityId, capabilityDetail(capabilityId)?.label)}
            onResearch={()=>setStage("research")}
            fallback={
              <CapabilityPanel
                key={capabilityId}
                level={level}
                field={field}
                capabilityId={capabilityId}
                onCapability={chooseCapability}
                onResearch={()=>setStage("research")}
              />
            }
          />
        )}
        {stage==="research" && field && <ResearchPanel field={field} capabilityId={capabilityId} onStudy={(s)=>{setSource(s);setStage("study")}}/>}
        {stage==="study" && source && <StudyPanel source={source} capabilityId={capabilityId} onEvidence={(d)=>{setDeep(d);setStage("evidence")}}/>}
        {stage==="evidence" && deep && <EvidencePanel deep={deep}/>}
      </section>
    </>}
    <footer><span>Needs Analysis · Research Runtime</span><span>Ascent = orientering, ikke bevist årsakskjede · Need ≠ capability ≠ horizon</span></footer>
  </main>;
}
